import { Injectable } from '@angular/core';

import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, pipe } from 'rxjs';

import { selectIsLinearBetslip } from '../../base/store/selectors';
import { IBetslipRootState } from '../../base/store/state';
import { BetslipType } from '../../core/betslip-type';
import { GroupPickId } from '../../core/picks/pick-id';
import BetbuilderActions from '../bet-builder/actions';
import BetslipComboBetActions from '../combo-bet/actions';
import { selectComboBetStake } from '../combo-bet/selectors';
import { selectBetBuilderPicks, selectBetBuilderTypeStatePicks } from '../linear-bet-builder/selectors';
import { ensureLinearBetslip, ensureLinearMode } from '../linear/helpers';
import BetslipPickActions from '../picks/actions';
import { ofTypeReplaceExistingPick } from '../picks/helpers';
import { betslipPicksListSelector } from '../picks/selectors';
import { QuickBetActions } from '../quick-bet/quick-bet-actions';
import { selectIsQuickBetInOverlay, selectQuickBetContext } from '../quick-bet/quick-bet.selectors';
import BetslipSingleBetActions from '../single-bet/actions';
import BetslipSystemBetActions from '../system-bet/actions';
import { BetslipTypesActions } from '../types/actions';
import { betslipTypesBaseSelector } from '../types/base/selectors';
import { selectComboPicksCount } from '../types/selectors';
import BetValidationService from '../validation/services/composition/bets/bet-validation.service';

@Injectable()
export class LinearDefaultStakeEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private betValidationService: BetValidationService,
        private userSettings: SyncUserSettingsService,
    ) {}

    notifyStakeChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipSingleBetActions.setSingleBetStake,
                BetslipSingleBetActions.setSingleBetPickStake,
                BetslipSystemBetActions.setSystemBetStake,
                BetslipComboBetActions.setComboBetStake,
                BetbuilderActions.setPickStake,
            ),
            ensureLinearBetslip(),
            concatLatestFrom(() => this.store.select(selectIsLinearBetslip)),
            filter(([action, isLinear]) => action.silent === false && isLinear),
            map((_) => BetslipTypesActions.setSelectedByUser({ isSelectedByUser: true })),
        );
    });

    triggerDefaultStakeCheckOnRemove$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            ensureLinearBetslip(),
            this.triggerDefaultStakeCheck(),
        );
    });

    triggerDefaultStakeCheckOnQuickBetPickAndQuickBetClose$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.addPick, QuickBetActions.closeQuickBet),
            ensureLinearMode(this.store),
            this.triggerDefaultStakeCheck(),
        );
    });

    triggerDefaultStakeCheckOnReplace$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            ensureLinearBetslip(),
            filter((action) => !action.atBetPlacement),
            this.triggerDefaultStakeCheck(),
        );
    });

    triggerDefaultStakeCheckOnAddGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetbuilderActions.addPick),
            ensureLinearBetslip(),
            filter((action) => GroupPickId.isId(action.pickId)),
            this.triggerDefaultStakeCheck(),
        );
    });

    clearComboBetDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearBetslip(),
            filter((action) => !action.isValidComboBet || action.isQuickBetBuilder),
            concatLatestFrom(() => this.store.select(selectComboBetStake)),
            filter(([_, comboStake]) => !!comboStake),
            map(() => BetslipComboBetActions.setComboBetStake({ stake: null })),
        );
    });

    clearBetBuilderDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearMode(this.store),
            filter((action) => action.isValidComboBet && action.hasBetBuilderPicks && !action.isQuickBetBuilder),
            map(() =>
                BetbuilderActions.setPickStakes({
                    stake: null,
                }),
            ),
        );
    });

    clearSingleBetDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearMode(this.store),
            filter((action) => action.isValidComboBet || action.hasBetBuilderPicks),
            map(() => BetslipSingleBetActions.setSingleBetStake({ stake: null })),
        );
    });

    setComboBetDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearMode(this.store),
            filter((action) => action.isValidComboBet && !action.isQuickBetBuilder),
            concatLatestFrom(() => this.store.select(selectComboBetStake)),
            filter(([_, comboStake]) => !comboStake),
            map(() => BetslipComboBetActions.setComboBetStake({ stake: this.userSettings.defaultStake })),
        );
    });

    setBetBuilderDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearMode(this.store),
            filter((action) => !action.isValidComboBet && !action.isQuickBetBuilder && action.hasBetBuilderPicks),
            map(() =>
                BetbuilderActions.setPickStakes({
                    stake: this.userSettings.defaultStake,
                }),
            ),
        );
    });

    setQuickBetBuilderDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearMode(this.store),
            filter((action) => action.isQuickBetBuilder && action.hasBetBuilderPicks),
            concatLatestFrom(() => [this.store.select(selectQuickBetContext), this.store.select(selectBetBuilderTypeStatePicks)]),
            filter(
                ([, quickBetBetslipType, picks]) =>
                    quickBetBetslipType.betslipType !== BetslipType.Combo && !!picks[quickBetBetslipType.pickId.toString()],
            ),
            map(([, quickBetBetslipType]) =>
                BetbuilderActions.setPickStake({
                    pickId: quickBetBetslipType.betslipType !== BetslipType.Combo ? quickBetBetslipType.pickId!.toString() : '',
                    stake: this.userSettings.defaultStake,
                }),
            ),
        );
    });

    setSingleBetDefaultStake$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipTypesActions.checkDefaultStake),
            ensureLinearMode(this.store),
            filter((action) => !action.isValidComboBet && !action.hasBetBuilderPicks),
            map(() => BetslipSingleBetActions.setSingleBetStake({ stake: this.userSettings.defaultStake })),
        );
    });

    clearComboStakeOnPickRemove$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipComboBetActions.removePick, BetslipPickActions.removeTypePicks),
            ensureLinearMode(this.store),
            concatLatestFrom(() => this.store.select(selectComboPicksCount)),
            filter(([_, picksCount]) => picksCount < 2),
            map(() => BetslipComboBetActions.setComboBetStake({ stake: null })),
        );
    });

    private triggerDefaultStakeCheck() {
        return pipe(
            concatLatestFrom(() => [this.store.select(selectIsLinearBetslip), this.store.select(betslipTypesBaseSelector)]),
            filter(([_, isLinear, types]) => isLinear && !types.selectedByUser),

            concatLatestFrom(() => [
                this.store.select(betslipPicksListSelector),
                this.store.select(selectBetBuilderPicks),
                this.store.select(selectIsQuickBetInOverlay),
            ]),
            map(([_, pickList, betBuilderPicks, inOverlay]) => ({
                isValidCombo: pickList.length > 1 && this.betValidationService.checkComboBet(pickList),
                hasBabPicks: betBuilderPicks.length > 0,
                isQuickBetBuilder: betBuilderPicks.length > 0 && inOverlay,
            })),
            map(({ isValidCombo, hasBabPicks, isQuickBetBuilder }) =>
                BetslipTypesActions.checkDefaultStake({ isValidComboBet: isValidCombo, hasBetBuilderPicks: hasBabPicks, isQuickBetBuilder }),
            ),
        );
    }
}
